/** Small building blocks from spec section 3: badges, pills, marks, meters, headers. */

import type { ReactNode } from 'react';
import type { AgentState, RequestType, Tier, TierTopic, VerifierVerdict } from '../api/types.js';
import { Link } from '../app/router.js';
import { budgetPace, formatDuration } from '../lib/format.js';
import { Icon, type IconName } from './icons.js';

/** T3 is an ink outline chip, T4 an accent fill with a lock. */
export function TierBadge({
  tier,
  topic,
  variant = 'full',
}: {
  tier: Tier;
  topic: TierTopic;
  /** full "T3 · DIŞ" (20h), detail (22h), mini "T3 DIŞ" (16h inline), mini-tier "T3" only. */
  variant?: 'full' | 'detail' | 'mini' | 'mini-tier';
}) {
  const t4 = tier === 'T4';
  const text = variant === 'mini-tier' ? tier : variant === 'mini' ? `${tier} ${topic}` : `${tier} · ${topic}`;
  const mini = variant === 'mini' || variant === 'mini-tier';
  return (
    <span
      className={`tier tier--${t4 ? 't4' : 't3'}${mini ? ' tier--mini' : ''}${variant === 'detail' ? ' tier--detail' : ''}`}
    >
      {t4 ? <Icon name="lock" size={mini ? 10 : 12} stroke={mini ? 2.5 : 2.25} /> : null}
      {text}
    </span>
  );
}

/** Queue row tier column: the chip over its topic key. */
export function TierStack({ tier, topic }: { tier: Tier; topic: TierTopic }) {
  const t4 = tier === 'T4';
  return (
    <span className="tier-stack">
      <span className={`tier tier--${t4 ? 't4' : 't3'}`}>
        {t4 ? <Icon name="lock" size={12} stroke={2.25} /> : null}
        {tier}
      </span>
      <span className={`key tier-stack__topic${t4 ? ' tier-stack__topic--t4' : ''}`}>{topic}</span>
      <span className="sr-only">{t4 ? ', para, karar senin' : ', dış iletişim'}</span>
    </span>
  );
}

const PILL_LABELS: Record<AgentState, string> = {
  working: 'Çalışıyor',
  waiting: 'Bekliyor',
  idle: 'Boşta',
  paused: 'Duraklatıldı',
  error: 'Hata',
};

function WaitingGlyph() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
      <circle cx="5" cy="5" r="4.1" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5 5V0.9A4.1 4.1 0 0 1 9.1 5Z" fill="currentColor" />
    </svg>
  );
}

function IdleGlyph() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
      <circle cx="5" cy="5" r="4.1" fill="none" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

function TriangleGlyph({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 10 10" aria-hidden="true" focusable="false">
      <path d="M5 1L9.5 9H0.5Z" fill="currentColor" />
    </svg>
  );
}

/** Agent state by fill style plus a word: solid, outline, dashed, grey, red. */
export function StatusPill({ state }: { state: AgentState }) {
  return (
    <span className={`pill pill--${state}`}>
      {state === 'working' ? <span className="pill__pulse" aria-hidden="true" /> : null}
      {state === 'waiting' ? <WaitingGlyph /> : null}
      {state === 'idle' ? <IdleGlyph /> : null}
      {state === 'paused' ? <Icon name="pause" size={10} stroke={2.5} /> : null}
      {state === 'error' ? <TriangleGlyph /> : null}
      {PILL_LABELS[state]}
    </span>
  );
}

/** The Denetçi field: passed (teal check), rejected (red cross), unverified (amber triangle). */
export function ResultMark({ verdict, children }: { verdict: VerifierVerdict; children?: ReactNode }) {
  if (verdict === 'passed') {
    return (
      <span className="mark mark--passed">
        <Icon name="check" size={12} stroke={2.5} className="mark__glyph" />
        {children ?? 'Denetçi geçti'}
      </span>
    );
  }
  if (verdict === 'rejected') {
    return (
      <span className="mark mark--rejected">
        <Icon name="x" size={12} stroke={2.5} />
        {children ?? 'Denetçi reddetti'}
      </span>
    );
  }
  return (
    <span className="mark mark--unverified">
      <TriangleGlyph />
      {children ?? 'Kontrol edilmedi'}
    </span>
  );
}

export function LiveDot({ hollow = false }: { hollow?: boolean }) {
  return <span className={hollow ? 'live-dot live-dot--hollow' : 'live-dot'} aria-hidden="true" />;
}

/**
 * Budget or progress bar. Fill is always the spent (or reached) amount; a tick marks how far
 * the month is. Fill turns amber when spending runs 10 points ahead of the month, red at 100%.
 */
export function Meter({
  value,
  tick = null,
  height = 4,
  label,
  pace = false,
  className,
}: {
  value: number;
  tick?: number | null;
  height?: 3 | 4 | 6;
  label: string;
  /** Colour the fill by budget pace (amber ahead, red over). */
  pace?: boolean;
  className?: string;
}) {
  const state = pace && tick !== null ? budgetPace(value, tick) : 'ok';
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    // biome-ignore lint/a11y/useSemanticElements: <meter> cannot draw the month tick; role="meter" keeps the semantics.
    <div
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
      aria-label={label}
      className={`meter meter--h${height}${className ? ` ${className}` : ''}`}
    >
      <span className={`meter__fill meter__fill--${state}`} style={{ width: `${pct}%` }} />
      {tick !== null ? <span className="meter__tick" style={{ left: `${tick * 100}%` }} /> : null}
    </div>
  );
}

/** Open-ended step trail: done steps solid, the current one hatched, no invented future. */
export function StepTrail({
  done,
  current,
  elapsedSeconds,
  showElapsed = true,
}: {
  done: number;
  current: string;
  elapsedSeconds: number;
  showElapsed?: boolean;
}) {
  const segments = Array.from({ length: done }, (_, index) => `done-${index}`);
  return (
    <div className="trail">
      <span className="trail__segments" aria-hidden="true">
        {segments.map((id) => (
          <span key={id} className="trail__seg" />
        ))}
        <span className="trail__seg trail__seg--current" />
      </span>
      <span className="trail__text">
        <span className="trail__step">
          <span className="mono">{done + 1}.</span> adım ·
        </span>
        <span className="ellipsis">{current}</span>
        {showElapsed ? (
          <span className="trail__elapsed">
            · <span className="mono">{formatDuration(elapsedSeconds)}</span>
          </span>
        ) : null}
      </span>
    </div>
  );
}

export function SectionHeader({
  id,
  title,
  count,
  action,
  right,
  level = 2,
  className,
}: {
  id?: string;
  title: string;
  count?: number | null;
  action?: { label: string; to: string } | null;
  right?: ReactNode;
  level?: 2 | 3;
  className?: string;
}) {
  const Heading = level === 2 ? 'h2' : 'h3';
  return (
    <div className={`section-header${className ? ` ${className}` : ''}`}>
      <Heading id={id} className="section-header__title">
        {title}
      </Heading>
      {count !== undefined && count !== null ? <span className="section-header__count mono">{count}</span> : null}
      {right}
      {action ? (
        <Link to={action.to} className="section-header__action">
          {action.label}
          <Icon name="chevron-right" size={16} />
        </Link>
      ) : null}
    </div>
  );
}

const TYPE_CHIPS: Record<RequestType, { icon: IconName; full: string; short: string }> = {
  question: { icon: 'message-circle-question', full: 'Soru · sadece sen', short: 'Soru' },
  design: { icon: 'pen-tool', full: 'Tasarım · yeni ürün', short: 'Tasarım' },
  photo: { icon: 'camera', full: 'Görsel · çekim', short: 'Görsel' },
};

export function TypeChip({ type, variant = 'full' }: { type: RequestType; variant?: 'full' | 'short' | 'plain' }) {
  const chip = TYPE_CHIPS[type];
  if (variant === 'short') return <span className="type-chip type-chip--short key">{chip.short}</span>;
  return (
    <span className="type-chip key">
      <Icon name={chip.icon} size={12} />
      {variant === 'plain' ? chip.short : chip.full}
    </span>
  );
}

export function Kbd({ children, onInk = false }: { children: ReactNode; onInk?: boolean }) {
  return <kbd className={onInk ? 'kbd kbd--on-ink' : 'kbd'}>{children}</kbd>;
}

/** A dot-separated meta line where each part may hold mono digits. */
export function Dot() {
  return <span aria-hidden="true"> · </span>;
}

export { TriangleGlyph };
